<template>
  <div>
    <!-- Confirmation Modal -->
    <ConfirmationModal
      :title="confirmationTitle"
      :message="confirmationMessage"
      confirmColor="error darken-1"
      ref="confirmationModal"
    />
    
    <!-- Debt Modal-->
    <v-dialog
      width="480px"
      :overlay-opacity="0.90"
      v-model="dialog"
    >
      <v-card>
        <v-card-title class="d-block">
          <div class="font-weight-bold">{{item.id ? "Update" : "Add"}} Debt</div>
          <p class="caption mb-4">Enter debt type, amount and description details below.</p>
        </v-card-title>
        <v-card-text class="py-2">

          <v-form
            @submit.prevent="addItem()"
          >
            <ValidationObserver ref="validationObserver">

              <ValidationProvider
                name="Debt Type"
                :rules="'required'"
                v-slot="{ errors }"
              >
                <v-select
                  outlined
                  dense
                  :autofocus="true"
                  label="Debt Type"
                  placeholder="Select debt type"
                  :hint="item.hint || ''"
                  persistent-hint
                  :items="debtTypes"
                  item-text="accountName"
                  item-value="accountName"
                  v-model="item.accountName"
                  :error-messages="errors"
                >
                  <template v-slot:label>
                    Debt Type
                    <span class="red--text">*</span>
                  </template>
                </v-select>
              </ValidationProvider>

              <ValidationProvider
                name="Amount"
                :rules="'required|min_value:0|max_value:999999999999'"
                v-slot="{ errors }"
              >
                <vuetify-money
                  type="number"
                  outlined
                  dense
                  required
                  :disabled="!!item.hint"
                  label="Amount"
                  v-model="item.monthlyAmount"
                  :error-messages="errors"
                  :options="VuetifyMoneyOptions"
                />
              </ValidationProvider>
              
              <ValidationProvider
                name="Description"
                :rules="'max:1500'"
                v-slot="{ errors }"
              >
                <v-text-field
                  outlined
                  dense
                  label="Debt Description"
                  v-model="item.description"
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </ValidationObserver>

            <v-card-actions class="pt-5 justify-end">
              <v-btn
                @click="closeModal()"
                min-width="120"
                min-height="42"
                depressed
                class="text-capitalize"
              >Cancel</v-btn>
              <v-btn
                type="submit"
                min-width="120"
                min-height="42"
                depressed
                color="primary"
                class="text-capitalize"
              >Save</v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    
    <v-row align="center" justify="space-between">
      <v-col cols="12" md="5"><h3>Debt</h3></v-col>
      <v-col cols="12" md="6" class="text-md-right">
        <v-btn
          @click="openModal"
          class="text-capitalize"
          depressed
          color="primary"
          >Add Debt</v-btn
        >
      </v-col>
    </v-row>
    <v-card class="mt-5" outlined rounded="lg">
      <v-data-table :items="debts" hide-default-footer :headers="headers">
        <!-- Customized No Data slot -->
        <template #no-data>
          <v-row justify="center">
            <v-col>
              <NoData />
              <p class="text-center mt-5">No Debt Items</p>
            </v-col>
          </v-row>
        </template>
        
        <template #[`item.accountName`]="{ item }">
          <div class="my-4">
            <span class="text-capitalize">{{item.accountName}}</span>
          </div>
        </template>
        
        <template #[`item.monthlyAmount`]="{ item }">
          {{asMoney(item.monthlyAmount)}}
        </template>
        
        <template #[`item.description`]="{ item }">
          <span class="text-wrap text-capitalize">{{item.description || "No description"}}</span>
        </template>
        
        <!-- Action slot -->
        <template #[`item.actions`]="{ item }">
          <v-btn
            @click="openModal(item)"
            class="text-capitalize"
            text
            color="primary"
          >
            Edit</v-btn>
          <v-btn
            v-if="!item.hint"
            @click="removeItem(item)"
            class="text-capitalize"
            text
            color="error"
          >
            Remove</v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import PageMixin from '@/mixins/page.mixin';
import { VuetifyMoneyOptions } from '@/lib/constants/options';
import { cloneDeep } from 'lodash';
import { v4 } from 'uuid';

export default {
  mixins: [PageMixin],
  data() {
    return {
      /** @type {import('vuetify').DataTableHeader[]}*/
      headers: [
        { text: 'Debt', value: 'accountName', sortable: false },
        { text: 'Amount', value: 'monthlyAmount', sortable: false },
        { text: 'Description', value: 'description', sortable: false },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false }
      ],
      dialog: false,
      item: {},

      VuetifyMoneyOptions: VuetifyMoneyOptions,
      confirmationTitle: "",
      confirmationMessage: "",
    };
  },

  props: {
    debts: {
      type: Array,
      default: () =>([])
    },
    debtTypes: {
      type: Array,
      default: () =>([])
    } 
  },

  methods: {
    openModal(item) {
      this.$refs.validationObserver?.reset();
      this.item = cloneDeep(item) || {};
      this.dialog = true;
    },

    closeModal(item) {
      this.dialog = false;
      this.item = item || {};
      this.$refs.validationObserver?.reset();
    },

    async addItem() {
      const debts = cloneDeep(this.debts)
      const valid = await this.$refs.validationObserver.validate();
      if(!valid) return;
      
      if(this.item.id) {
        const index = debts.findIndex((el) => el.id == this.item.id)
        debts.splice(index, 1, {...this.item});
      } else {
        this.item.id = v4(); 
        debts.push(this.item);
      }

      this.$emit('update', debts) ;
      this.closeModal();
    },
  
    removeItem(item) {
      this.confirmationTitle = `Confirm Deletion`;
      this.confirmationMessage = `Are you sure you want to delete this debt '${item.accountName}'?`;
      this.$refs.confirmationModal.show(async () => {
        const debts = this.debts.filter((el) => el.id !== item.id)
        this.$emit('update', debts) 
      });
    }
  }
};
</script>
